// src/components/SaleRegistrationModal.js

import React, { useState, useEffect } from "react";
import axios from "axios";
import "./SaleRegistrationModal.css"; // Import the new CSS file
import { useSelector, useDispatch } from "react-redux";
import { selectTokens } from "../redux/userSlice";
import {
  Modal,
  Button,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Dropdown,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import {} from "../redux/registrationSlice";
import {
  registerForSaleGlobalRegistrationByUserId,
} from '../redux/registrationSlice';

const SaleRegistrationModal = ({ isOpen, onClose, saleId, saleDate, registeredUsers }) => {
  const dispatch = useDispatch();
  const tokens = useSelector(selectTokens);
  const baseUrl = process.env.REACT_APP_SERVER_URL;
  const apiKey = process.env.REACT_APP_SERVER_API_KEY;

  const [searchText, setSearchText] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showRegistrationToast, setShowRegistrationToast] = useState(false);
  const [disableRegister, setDisableRegister] = useState(false);

  useEffect(() => {
    if (searchText.length >= 2) {
      const fetchUsers = async () => {
        try {
          const response = await axios.get(
            `${baseUrl}/app/messages/users?search=${searchText}`,
            {
              headers: {
                Authorization: `Bearer ${tokens.accessToken}`,
                "x-api-key": apiKey,
              },
            }
          );
          setUsers(response.data || []);
          setShowDropdown(response.data && response.data.length > 0);
        } catch (error) {
          console.error("Error fetching users:", error);
          setShowDropdown(false);
        } finally {
          setIsLoading(false);
        }
      };

      fetchUsers();
    } else {
      setUsers([]);
      setShowDropdown(false);
    }
  }, [searchText, baseUrl, tokens.accessToken, apiKey]);

  const handleSearchChange = (e) => setSearchText(e.target.value);

  const selectUser = async (user) => {
    setDisableRegister(false)
    setSelectedUser(user);
    setSearchText(`${user.first_name} ${user.last_name}`);
    setShowDropdown(false);
  };

  // Register user for a sale with Global Registration
  const handleRegister = async () => {
    if (!selectedUser || !saleId) {
      alert("All fields are required.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await dispatch(registerForSaleGlobalRegistrationByUserId({ saleId, saleDate, selectedUser })).unwrap();
      
      if (response.status === 201) {
        // alert("User successfully registered for the sale.");
        setShowRegistrationToast(true)
        setDisableRegister(true)

      }
    } catch (error) {
      console.error("Error registering for sale:", error);
      alert("Failed to register for the sale. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    setDisableRegister(false);
    setSelectedUser(null);
    setSearchText("");
    onClose();
  };

  const alreadyRegistered = (user) => {
    let isAlreadyRegistered = false;
    registeredUsers.forEach(registeredUser => {
      if (registeredUser.fusionauth_user_id === user.fusionauth_user_id) {
        isAlreadyRegistered = true;
      }
    }) 
    return isAlreadyRegistered;
  }


  return (
    <Modal
      show={isOpen}
      onHide={handleCloseModal}
      centered
      className="sale-reg-modal"
      size="lg"
    >
      <Modal.Header closeButton className="sale-reg-modal-header">
        <Modal.Title>
          <FontAwesomeIcon icon={faUserPlus} className="sale-reg-modal-icon" />{" "}
          Add Registration
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="sale-reg-modal-body">
        <Form>
          <Form.Group
            controlId="saleReg-saleId"
            className="sale-reg-form-group"
          >
            <Form.Label>Sale ID</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faUserPlus} />
              </InputGroup.Text>
              <Form.Control type="text" value={saleId} readOnly />
            </InputGroup>
          </Form.Group>
          <InputGroup className="search-input">
            <Form.Control
              type="text"
              placeholder="Search users (name, email, phone, etc.)"
              value={searchText}
              onChange={handleSearchChange}
              style={{height: "2.5em"}}
            />
            {showDropdown && (
              <Dropdown show={showDropdown} alignRight>
                <Dropdown.Toggle variant="success" id="dropdown-basic" className="mt-0" style={{height: "2.5em"}}>
                  Search Results
                </Dropdown.Toggle>
                <Dropdown.Menu
                  style={{ maxHeight: "300px", overflowY: "auto" }}
                >
                  {users.map((user) => (
                    <Dropdown.Item
                      key={user.fusionauth_user_id}
                      onClick={() => selectUser(user)}
                      disabled={alreadyRegistered(user)}
                    >
                      {`${user.first_name} ${user.last_name} - ${user.email}`}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}
          </InputGroup>
          {selectedUser && (
            <div className="sale-reg-selected-user">
              <Alert variant="info" className="sale-reg-selected-user-alert">
                <strong>Selected User:</strong> {selectedUser.first_name}{" "}
                {selectedUser.last_name} - {selectedUser.email}
              </Alert>
              <Button
                variant="primary"
                onClick={handleRegister}
                className="sale-reg-confirm-btn"
                disabled={isLoading || disableRegister}
              >
                {isLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />{" "}
                    Registering...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faUserPlus} /> Register for Sale
                  </>
                )}
              </Button>
            </div>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer className="sale-reg-modal-footer">
        <Button
          variant="secondary"
          onClick={onClose}
          className="sale-reg-close-btn"
        >
          <FontAwesomeIcon icon={faTimes} /> Close
        </Button>
      </Modal.Footer>

      {/* On Success Toast Notification */}
      <ToastContainer
        position="top-end"
        className="p-3 unique-auction-toast-container"
      >
        <Toast
          onClose={() => setShowRegistrationToast(false)}
          show={showRegistrationToast}
          delay={8000}
          autohide
          bg="warning"
        >
          <Toast.Header>
            <strong className="me-auto">Profile / Registration Notice</strong>
          </Toast.Header>
          <Toast.Body>
            <div style={{ marginBottom: "10px" }}>
              Successfully registered user {selectedUser?.first_name} {selectedUser?.last_name} to
              sale ID: {saleId}
            </div>
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </Modal>
  );
};

export default SaleRegistrationModal;
